import { Button, Card } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { RouteConstants } from 'app/routes';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { ITableSort } from 'types';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import { useGetRecipeQuery } from '../../../../api';
import { getRecipeColumns } from '../../helpers';
import { IRecipe, IRecipeFilters } from '../../types';

interface IProps {
  filters: IRecipeFilters;
}

const RecipeCard: FC<IProps> = ({ filters }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { mobileOrTablet } = useResponsiveQuery();

  const envId = useSelector(selectSelectedEnvId) as number;

  const [sort, setSort] = useState<ITableSort>({});

  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const { data: recipeResponse, isFetching } = useGetRecipeQuery(
    {
      envId,
      pageSize,
      currentPage: currentPage - 1,
      filters,
      sort,
    },
    { skip: !envId },
  );

  useEffect(() => {
    if (recipeResponse?.totalPages && currentPage > recipeResponse.totalPages) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeResponse?.totalPages]);

  const columns: ColumnsType<IRecipe> = getRecipeColumns(t);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: unknown,
    sorter: SorterResult<IRecipe> | SorterResult<IRecipe>[],
  ) => {
    if ('field' in sorter && sorter.field && sorter.order) {
      const { field, order } = sorter;
      setSort({ field: field as string | string[], order });
    } else {
      setSort({});
    }

    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  return (
    <Card
      bordered={false}
      title={t('Recipe.Recipes', 'Recipes')}
      style={{
        maxHeight: mobileOrTablet ? 'unset' : 'calc(100vh - 163px)',
        overflow: 'auto',
      }}
      extra={[
        <Button
          type="primary"
          onClick={() =>
            history.push(
              generatePath(RouteConstants.recipe.create, {
                envId,
              }),
            )
          }
        >
          {t('Common.Add', 'Add')}
        </Button>,
      ]}
    >
      <StyledTable
        onRow={recipe => ({
          onClick: () =>
            history.push(
              generatePath(RouteConstants.recipe.details, {
                envId: envId as number,
                recipeId: recipe.id,
              }),
            ),
        })}
        rowKey="id"
        dataSource={recipeResponse?.content}
        columns={columns}
        loading={isFetching}
        pagination={{
          current: currentPage,
          total: recipeResponse?.totalElements,
          pageSize,
        }}
        onChange={onTableChange}
      />
    </Card>
  );
};

export default RecipeCard;
